// react
import { useCallback, useEffect, useState } from 'react';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

/**
 * Custom hook for managing comparison node IDs in URL parameters
 * @param {Object} options - Configuration options
 * @param {string} options.paramKey - URL parameter key (default: 'comparisonNodes')
 * @returns {Object} - { comparisonNodeIds, addComparisonNode, removeComparisonNode, updateComparisonNode, clearComparisonNodes }
 */
export default function useUrlComparisonNodes(options = {}) {
  const { paramKey = 'comparisonNodes' } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  // Read comparison node IDs from URL
  const readFromUrl = useCallback(() => {
    const raw = searchParams.get(paramKey);
    if (!raw) return [];
    try {
      const decoded = decodeURIComponent(raw);
      const parsed = JSON.parse(decoded);
      // Ensure it's an array (allow null values for empty slots)
      if (Array.isArray(parsed)) {
        return parsed.filter((id) => id !== undefined && id !== '');
      }
      return [];
    } catch {
      return [];
    }
  }, [searchParams, paramKey]);

  const [comparisonNodeIds, setComparisonNodeIdsState] = useState(readFromUrl);

  // Sync when user navigates back/forward
  useEffect(() => {
    const next = readFromUrl();
    if (JSON.stringify(next) !== JSON.stringify(comparisonNodeIds)) {
      setComparisonNodeIdsState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, paramKey]);

  // Write comparison node IDs to URL
  const writeToUrl = useCallback(
    (nodeIds, { push = false } = {}) => {
      // Allow null values (for empty slots), but filter out undefined and empty strings
      const validNodeIds = Array.isArray(nodeIds)
        ? nodeIds.filter((id) => id !== undefined && id !== '')
        : [];

      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (validNodeIds.length === 0) {
            sp.delete(paramKey);
          } else {
            sp.set(paramKey, encodeURIComponent(JSON.stringify(validNodeIds)));
          }
          return sp;
        },
        { replace: !push },
      );
      setComparisonNodeIdsState(validNodeIds);
    },
    [setSearchParams, paramKey],
  );

  // Add a comparison node ID (can be null for empty slots)
  const addComparisonNode = useCallback(
    (nodeId = null) => {
      writeToUrl([...comparisonNodeIds, nodeId]);
    },
    [comparisonNodeIds, writeToUrl],
  );

  // Remove a comparison node ID by index
  const removeComparisonNode = useCallback(
    (index) => {
      const updated = comparisonNodeIds.filter((_, idx) => idx !== index);
      writeToUrl(updated);
    },
    [comparisonNodeIds, writeToUrl],
  );

  // Update a comparison node ID at a specific index
  const updateComparisonNode = useCallback(
    (index, nodeId) => {
      if (index < 0 || index >= comparisonNodeIds.length) return;
      const updated = [...comparisonNodeIds];
      updated[index] = nodeId;
      writeToUrl(updated);
    },
    [comparisonNodeIds, writeToUrl],
  );

  // Clear all comparison nodes
  const clearComparisonNodes = useCallback(() => {
    writeToUrl([]);
  }, [writeToUrl]);

  // Set all comparison node IDs at once
  const setComparisonNodeIds = useCallback(
    (nodeIds) => {
      writeToUrl(nodeIds);
    },
    [writeToUrl],
  );

  return {
    comparisonNodeIds,
    addComparisonNode,
    removeComparisonNode,
    updateComparisonNode,
    clearComparisonNodes,
    setComparisonNodeIds,
  };
}
